import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { faqContent } from "@/i18n/faq";
import { defaultLocale, isSupportedLocale } from "@/i18n/routing";

export function FAQ() {
  const { i18n } = useTranslation();
  const [openItems, setOpenItems] = useState<Set<number>>(new Set());
  const resolvedLanguage = i18n.resolvedLanguage ?? "";
  const locale = isSupportedLocale(resolvedLanguage)
    ? resolvedLanguage
    : defaultLocale;
  const content = faqContent[locale];

  const toggleItem = (id: number) => {
    setOpenItems((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <section id="faq" className="py-20 bg-white" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-poppins font-bold text-4xl md:text-5xl text-dark mb-6" data-testid="faq-title">
            {content.title} <span className="text-primary">{content.titleHighlight}</span>
          </h2>
          <p className="text-xl text-gray-600" data-testid="faq-subtitle">
            {content.subtitle}
          </p>
        </div>

        <div className="divide-y divide-gray-200 border-y border-gray-200">
          {content.items.map((item, index) => {
            const isOpen = openItems.has(index);
            const answerId = `faq-answer-${index}`;

            return (
              <div key={item.question} data-testid={`faq-item-${index + 1}`}>
                <button
                  type="button"
                  onClick={() => toggleItem(index)}
                  className="flex w-full items-center justify-between gap-4 py-6 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  aria-expanded={isOpen}
                  aria-controls={answerId}
                  data-testid={`faq-button-${index + 1}`}
                >
                  <span className="font-semibold text-lg text-dark">
                    {item.question}
                  </span>
                  <ChevronDown
                    className={`h-5 w-5 flex-shrink-0 text-gray-500 transition-transform duration-200 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                    aria-hidden="true"
                  />
                </button>
                <div
                  id={answerId}
                  className="pb-6"
                  hidden={!isOpen}
                  data-testid={`faq-answer-${index + 1}`}
                >
                  <p className="text-gray-600 leading-relaxed">{item.answer}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
